import React, { useState } from 'react'
import { FlipHorizontal, FlipVertical, Scissors } from 'lucide-react'
import FitToPageButton from './FitToPageButton'
import AnimationPropertiesPanel from './AnimationPropertiesPanel'
import { AIFieldInput } from './CommonElementProperties'
import { templateAPI } from '../../services/api'
import MediaPreview from './MediaPreview'

/**
 * Image Element Properties Component
 * Handles the properties panel for image elements including transparency, brightness, contrast, intensity, and flip options
 */
const ImageElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage
}) => {
  // Removed blocking dialog; use snackbar-based async flow triggered via window event
  const [enhancingPrompt, setEnhancingPrompt] = useState(false)

  if (element.type !== 'image') {
    return null
  }

  const handleBackgroundRemovalComplete = (newImageUrl) => {
    // Replace the current image URL with the new background-removed image URL
    updateElementProperty(selectedElement, 'src', newImageUrl)
    updateElementProperty(selectedElement, 'url', newImageUrl)
    // Also update storageUrl if it exists
    if (element.storageUrl) {
      updateElementProperty(selectedElement, 'storageUrl', newImageUrl)
    }
  }

  return (
    <div className="space-y-4">
      {/* Media Preview — view inline or full size, and crop */}
      <MediaPreview
        element={element}
        selectedElement={selectedElement}
        updateElementProperty={updateElementProperty}
      />

      {/* Image Adjustments Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Image Adjustments</h3>
        <div className="space-y-3">
          {/* Transparency */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-gray-600 font-medium">Transparency</label>
              <button
                onClick={() => updateElementProperty(selectedElement, 'gradientTransparency',
                  element.gradientTransparency ? null : {
                    enabled: true,
                    type: 'linear',
                    angle: 0,
                    startOpacity: 1,
                    endOpacity: 0
                  }
                )}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  element.gradientTransparency?.enabled
                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                {element.gradientTransparency?.enabled ? 'Gradient' : 'Uniform'}
              </button>
            </div>

            {!element.gradientTransparency?.enabled ? (
              // Uniform Transparency
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={element.opacity ?? 1}
                  onChange={(e) => updateElementProperty(selectedElement, 'opacity', parseFloat(e.target.value))}
                  className="w-24"
                />
                <span className="text-xs text-gray-500 min-w-[35px]">{Math.round((element.opacity ?? 1) * 100)}%</span>
              </div>
            ) : (
              // Gradient Transparency
              <div className="space-y-3 pl-2 border-l-2 border-purple-200">
                {/* Gradient Type and Angle */}
                <div className="flex items-center space-x-2">
                  <select
                    value={element.gradientTransparency?.type || 'linear'}
                    onChange={(e) => updateElementProperty(selectedElement, 'gradientTransparency', {
                      ...element.gradientTransparency,
                      type: e.target.value
                    })}
                    className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="linear">Linear</option>
                    <option value="radial">Radial</option>
                  </select>

                  {element.gradientTransparency?.type === 'linear' && (
                    <div className="flex items-center space-x-1">
                      <span className="text-xs text-gray-500">Angle:</span>
                      <input
                        type="number"
                        min="0"
                        max="360"
                        value={element.gradientTransparency?.angle || 0}
                        onChange={(e) => updateElementProperty(selectedElement, 'gradientTransparency', {
                          ...element.gradientTransparency,
                          angle: parseInt(e.target.value)
                        })}
                        className="w-16 text-xs border border-gray-300 rounded px-1 py-1 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <span className="text-xs text-gray-400">°</span>
                    </div>
                  )}
                </div>

                {/* Start and End Opacity */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 block mb-1">Start Opacity</label>
                    <div className="flex items-center space-x-1">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={element.gradientTransparency?.startOpacity ?? 1}
                        onChange={(e) => updateElementProperty(selectedElement, 'gradientTransparency', {
                          ...element.gradientTransparency,
                          startOpacity: parseFloat(e.target.value)
                        })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-400 min-w-[30px]">
                        {Math.round((element.gradientTransparency?.startOpacity ?? 1) * 100)}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 block mb-1">End Opacity</label>
                    <div className="flex items-center space-x-1">
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={element.gradientTransparency?.endOpacity ?? 0}
                        onChange={(e) => updateElementProperty(selectedElement, 'gradientTransparency', {
                          ...element.gradientTransparency,
                          endOpacity: parseFloat(e.target.value)
                        })}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-400 min-w-[30px]">
                        {Math.round((element.gradientTransparency?.endOpacity ?? 0) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-2">
                  <label className="text-xs text-gray-500 block mb-1">Preview</label>
                  <div
                    className="w-full h-6 border border-gray-300 rounded"
                    style={{
                      background: element.gradientTransparency?.type === 'radial'
                        ? `radial-gradient(circle, rgba(0,0,0,${element.gradientTransparency?.startOpacity ?? 1}) 0%, rgba(0,0,0,${element.gradientTransparency?.endOpacity ?? 0}) 100%)`
                        : `linear-gradient(${element.gradientTransparency?.angle || 0}deg, rgba(0,0,0,${element.gradientTransparency?.startOpacity ?? 1}) 0%, rgba(0,0,0,${element.gradientTransparency?.endOpacity ?? 0}) 100%)`,
                      backgroundImage: 'url("data:image/svg+xml,%3csvg width=\'100%25\' height=\'100%25\' xmlns=\'http://www.w3.org/2000/svg\'%3e%3cdefs%3e%3cpattern id=\'checkerboard\' x=\'0\' y=\'0\' width=\'10\' height=\'10\' patternUnits=\'userSpaceOnUse\'%3e%3crect x=\'0\' y=\'0\' width=\'5\' height=\'5\' fill=\'%23ffffff\'/%3e%3crect x=\'5\' y=\'5\' width=\'5\' height=\'5\' fill=\'%23ffffff\'/%3e%3crect x=\'5\' y=\'0\' width=\'5\' height=\'5\' fill=\'%23cccccc\'/%3e%3crect x=\'0\' y=\'5\' width=\'5\' height=\'5\' fill=\'%23cccccc\'/%3e%3c/pattern%3e%3c/defs%3e%3crect width=\'100%25\' height=\'100%25\' fill=\'url(%23checkerboard)\'/%3e%3c/svg%3e")',
                      backgroundBlendMode: 'multiply'
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Brightness */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Brightness</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-100"
                max="100"
                value={element.brightness ?? 0}
                onChange={(e) => updateElementProperty(selectedElement, 'brightness', parseInt(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{element.brightness ?? 0}</span>
            </div>
          </div>

          {/* Contrast */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Contrast</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-100"
                max="100"
                value={element.contrast ?? 0}
                onChange={(e) => updateElementProperty(selectedElement, 'contrast', parseInt(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{element.contrast ?? 0}</span>
            </div>
          </div>

          {/* Intensity (Saturation) */}
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Intensity</label>
            <div className="flex items-center space-x-2">
              <input
                type="range"
                min="-100"
                max="100"
                value={element.intensity ?? 0}
                onChange={(e) => updateElementProperty(selectedElement, 'intensity', parseInt(e.target.value))}
                className="w-24"
              />
              <span className="text-xs text-gray-500 min-w-[35px]">{element.intensity ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dimensions & Properties Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Dimensions & Properties</h3>
        <div className="grid grid-cols-2 gap-3">
          {/* X Position */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">X</label>
            <input
              type="number"
              value={Math.round(element.x || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'x', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Y Position */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Y</label>
            <input
              type="number"
              value={Math.round(element.y || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'y', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Width */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Width</label>
            <input
              type="number"
              value={element.width || 100}
              onChange={(e) => updateElementProperty(selectedElement, 'width', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="10"
              max="2000"
            />
          </div>

          {/* Height */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Height</label>
            <input
              type="number"
              value={element.height || 100}
              onChange={(e) => updateElementProperty(selectedElement, 'height', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="10"
              max="2000"
            />
          </div>

          {/* Border Radius */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Radius</label>
            <input
              type="number"
              value={element.borderRadius || 0}
              onChange={(e) => updateElementProperty(selectedElement, 'borderRadius', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="100"
            />
          </div>

          {/* Rotation */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Rotation</label>
            <input
              type="number"
              value={Math.round(element.rotation || 0)}
              onChange={(e) => updateElementProperty(selectedElement, 'rotation', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="-360"
              max="360"
            />
          </div>

          {/* Layer */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Layer</label>
            <input
              type="number"
              value={element.layer || 0}
              onChange={(e) => updateElementProperty(selectedElement, 'layer', parseInt(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
              max="1000"
            />
          </div>
        </div>

        {/* Advanced Properties Section */}
        <div className="grid grid-cols-2 gap-3">
          {/* Blend Mode */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Blend</label>
            <select
              value={element.blendMode || 'normal'}
              onChange={(e) => updateElementProperty(selectedElement, 'blendMode', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="normal">Normal</option>
              <option value="multiply">Multiply</option>
              <option value="screen">Screen</option>
              <option value="overlay">Overlay</option>
              <option value="darken">Darken</option>
              <option value="lighten">Lighten</option>
              <option value="color-dodge">Color Dodge</option>
              <option value="color-burn">Color Burn</option>
              <option value="hard-light">Hard Light</option>
              <option value="soft-light">Soft Light</option>
              <option value="difference">Difference</option>
              <option value="exclusion">Exclusion</option>
            </select>
          </div>

          {/* Object Fit */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600 min-w-[50px]">Fit</label>
            <select
              value={element.objectFit || 'fill'}
              onChange={(e) => updateElementProperty(selectedElement, 'objectFit', e.target.value)}
              className="border border-gray-300 rounded px-2 py-1 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="fill">Fill (Stretch)</option>
              <option value="cover">Cover</option>
              <option value="contain">Contain</option>
              <option value="none">None</option>
              <option value="scale-down">Scale Down</option>
            </select>
          </div>
        </div>

        {/* Enhanced Shadow Configuration */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-600 font-medium">Drop Shadow</label>
            <input
              type="checkbox"
              checked={!!(element.shadow?.enabled)}
              onChange={(e) => {
                const shadowConfig = e.target.checked ? {
                  enabled: true,
                  offsetX: element.shadow?.offsetX || 4,
                  offsetY: element.shadow?.offsetY || 4,
                  blur: element.shadow?.blur || 8,
                  spread: element.shadow?.spread || 0,
                  color: element.shadow?.color || '#000000',
                  opacity: element.shadow?.opacity || 0.25,
                  inset: element.shadow?.inset || false,
                  gradient: element.shadow?.gradient || null
                } : { enabled: false }
                updateElementProperty(selectedElement, 'shadow', shadowConfig)
              }}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
          </div>

          {element.shadow?.enabled && (
            <div className="space-y-3 pl-4 border-l-2 border-gray-200">
              {/* Position Controls */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Offset X</label>
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={element.shadow?.offsetX || 4}
                    onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      offsetX: parseInt(e.target.value)
                    })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">{element.shadow?.offsetX || 4}px</span>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Offset Y</label>
                  <input
                    type="range"
                    min="-50"
                    max="50"
                    value={element.shadow?.offsetY || 4}
                    onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      offsetY: parseInt(e.target.value)
                    })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">{element.shadow?.offsetY || 4}px</span>
                </div>
              </div>

              {/* Blur and Spread */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Blur</label>
                  <input
                    type="range"
                    min="0"
                    max="50"
                    value={element.shadow?.blur || 8}
                    onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      blur: parseInt(e.target.value)
                    })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">{element.shadow?.blur || 8}px</span>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Spread</label>
                  <input
                    type="range"
                    min="-20"
                    max="20"
                    value={element.shadow?.spread || 0}
                    onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      spread: parseInt(e.target.value)
                    })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">{element.shadow?.spread || 0}px</span>
                </div>
              </div>

              {/* Shadow Type and Opacity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Type</label>
                  <select
                    value={element.shadow?.inset ? 'inset' : 'outset'}
                    onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      inset: e.target.value === 'inset'
                    })}
                    className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="outset">Drop Shadow</option>
                    <option value="inset">Inner Shadow</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Opacity</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={element.shadow?.opacity || 0.25}
                    onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      opacity: parseFloat(e.target.value)
                    })}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">{Math.round((element.shadow?.opacity || 0.25) * 100)}%</span>
                </div>
              </div>

              {/* Color Configuration */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs text-gray-500">Shadow Color</label>
                  <button
                    onClick={() => updateElementProperty(selectedElement, 'shadow', {
                      ...element.shadow,
                      gradient: element.shadow?.gradient ? null : {
                        type: 'linear',
                        angle: 45,
                        stops: [
                          { color: '#000000', position: 0 },
                          { color: '#666666', position: 100 }
                        ]
                      }
                    })}
                    className={`text-xs px-2 py-1 rounded transition-colors ${
                      element.shadow?.gradient
                        ? 'bg-purple-100 text-purple-700 border border-purple-300'
                        : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {element.shadow?.gradient ? 'Gradient' : 'Solid'}
                  </button>
                </div>

                {!element.shadow?.gradient ? (
                  // Solid Color
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      value={element.shadow?.color || '#000000'}
                      onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                        ...element.shadow,
                        color: e.target.value
                      })}
                      className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={element.shadow?.color || '#000000'}
                      onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                        ...element.shadow,
                        color: e.target.value
                      })}
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="#000000"
                    />
                  </div>
                ) : (
                  // Gradient Configuration
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <select
                        value={element.shadow?.gradient?.type || 'linear'}
                        onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                          ...element.shadow,
                          gradient: {
                            ...element.shadow.gradient,
                            type: e.target.value
                          }
                        })}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="linear">Linear</option>
                        <option value="radial">Radial</option>
                      </select>
                      {element.shadow?.gradient?.type === 'linear' && (
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-500">Angle:</span>
                          <input
                            type="number"
                            min="0"
                            max="360"
                            value={element.shadow?.gradient?.angle || 45}
                            onChange={(e) => updateElementProperty(selectedElement, 'shadow', {
                              ...element.shadow,
                              gradient: {
                                ...element.shadow.gradient,
                                angle: parseInt(e.target.value)
                              }
                            })}
                            className="w-16 text-xs border border-gray-300 rounded px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-400">°</span>
                        </div>
                      )}
                    </div>

                    {/* Gradient Stops */}
                    <div className="space-y-1">
                      {(element.shadow?.gradient?.stops || []).map((stop, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="color"
                            value={stop.color}
                            onChange={(e) => {
                              const newStops = [...(element.shadow?.gradient?.stops || [])]
                              newStops[index] = { ...stop, color: e.target.value }
                              updateElementProperty(selectedElement, 'shadow', {
                                ...element.shadow,
                                gradient: {
                                  ...element.shadow.gradient,
                                  stops: newStops
                                }
                              })
                            }}
                            className="w-6 h-6 border border-gray-300 rounded cursor-pointer"
                          />
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={stop.position}
                            onChange={(e) => {
                              const newStops = [...(element.shadow?.gradient?.stops || [])]
                              newStops[index] = { ...stop, position: parseInt(e.target.value) }
                              updateElementProperty(selectedElement, 'shadow', {
                                ...element.shadow,
                                gradient: {
                                  ...element.shadow.gradient,
                                  stops: newStops
                                }
                              })
                            }}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                          <span className="text-xs text-gray-400 w-8">{stop.position}%</span>
                          {(element.shadow?.gradient?.stops || []).length > 2 && (
                            <button
                              onClick={() => {
                                const newStops = (element.shadow?.gradient?.stops || []).filter((_, i) => i !== index)
                                updateElementProperty(selectedElement, 'shadow', {
                                  ...element.shadow,
                                  gradient: {
                                    ...element.shadow.gradient,
                                    stops: newStops
                                  }
                                })
                              }}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}

                      {(element.shadow?.gradient?.stops || []).length < 5 && (
                        <button
                          onClick={() => {
                            const stops = element.shadow?.gradient?.stops || []
                            const newPosition = stops.length > 0 ? Math.max(...stops.map(s => s.position)) + 20 : 50
                            const newStops = [...stops, { color: '#666666', position: Math.min(100, newPosition) }]
                            updateElementProperty(selectedElement, 'shadow', {
                              ...element.shadow,
                              gradient: {
                                ...element.shadow.gradient,
                                stops: newStops
                              }
                            })
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          + Add Color Stop
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Transform Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Transform</h3>
        <div className="space-y-3">
          {/* Flip Controls */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-600">Flip</label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => updateElementProperty(selectedElement, 'flipX', !element.flipX)}
                className={`px-3 py-2 border rounded-md transition-colors ${
                  element.flipX
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                title="Flip Horizontal"
              >
                <FlipHorizontal className="w-4 h-4" />
              </button>
              <button
                onClick={() => updateElementProperty(selectedElement, 'flipY', !element.flipY)}
                className={`px-3 py-2 border rounded-md transition-colors ${
                  element.flipY
                    ? 'bg-blue-100 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
                title="Flip Vertical"
              >
                <FlipVertical className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Scale Controls */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600 min-w-[50px]">Scale X</label>
              <input
                type="number"
                step="0.1"
                value={element.scaleX || 1}
                onChange={(e) => updateElementProperty(selectedElement, 'scaleX', parseFloat(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0.1"
                max="5"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-gray-600 min-w-[50px]">Scale Y</label>
              <input
                type="number"
                step="0.1"
                value={element.scaleY || 1}
                onChange={(e) => updateElementProperty(selectedElement, 'scaleY', parseFloat(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0.1"
                max="5"
              />
            </div>
          </div>
        </div>

        {/* Fit to Page Button */}
        <div className="mt-3">
          <FitToPageButton
            selectedElement={selectedElement}
            updateElementProperty={updateElementProperty}
            currentPage={currentPage}
            element={element}
          />
        </div>
      </div>

      {/* Actions Section */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Actions</h3>
        <div className="space-y-2">
          {/* Remove Background Button */}
          <button
            onClick={() => {
              const imageUrl = element.src || element.url || element.storageUrl
              const filename = element.name || element.filename || 'image.png'
              if (!imageUrl) return
              // Fire a global event; WorkspacePage listens and manages snackbar/polling
              window.dispatchEvent(new CustomEvent('background-removal:start', {
                detail: {
                  imageUrl,
                  filename,
                  elementId: selectedElement
                }
              }))
            }}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            disabled={!element.src && !element.url && !element.storageUrl}
          >
            <Scissors className="w-4 h-4" />
            <span>Remove Background</span>
          </button>
          {(!element.src && !element.url && !element.storageUrl) && (
            <p className="text-xs text-gray-500 text-center">Image URL required</p>
          )}

          {/* AI Image Transparency toggle. Default true; absent == false. */}
          <label className="flex items-center justify-between pt-1 cursor-pointer select-none">
            <span className="text-sm text-gray-600">AI transparent background</span>
            <input
              type="checkbox"
              checked={element.ai_image_transparency === true}
              onChange={(e) => updateElementProperty(selectedElement, 'ai_image_transparency', e.target.checked)}
              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
            />
          </label>
          <p className="text-xs text-gray-400">
            When on, AI-generated images have their background removed automatically.
          </p>
        </div>
      </div>

      {/* Snackbar-based background removal handled by WorkspacePage */}

      {/* AI Field (grouped together with AI Image Prompt) */}
      <AIFieldInput
        element={element}
        selectedElement={selectedElement}
        updateElementProperty={updateElementProperty}
      />

      {/* AI Image Prompt */}
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-700">AI Image Prompt</h3>
          <div className="flex items-center gap-2">
          {/* Generate Image — only shown when a prompt is present */}
          {(element.prompt || '').trim() && (
            <button
              onClick={() => {
                const prompt = (element.prompt || '').trim()
                if (!prompt) return
                // Fire a global event; WorkspacePage runs generation + optional
                // background removal and applies the result to this element.
                window.dispatchEvent(new CustomEvent('ai-image-generate:start', {
                  detail: {
                    elementId: selectedElement,
                    prompt,
                    width: Math.round(element.width) || undefined,
                    height: Math.round(element.height) || undefined,
                    transparency: element.ai_image_transparency === true,
                    filename: element.name || element.alt || 'ai-image.png'
                  }
                }))
              }}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              title="Generate this image from the prompt using AI"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span>Generate Image</span>
            </button>
          )}
          <button
            onClick={async () => {
              const currentPrompt = element.prompt || ''
              if (!currentPrompt.trim()) return
              setEnhancingPrompt(true)
              try {
                const resp = await templateAPI.enhancePrompt(currentPrompt.trim())
                if (resp.data.success || resp.data.status === 'success') {
                  const enhanced = resp.data.data?.prompt || ''
                  if (enhanced) {
                    updateElementProperty(selectedElement, 'prompt', enhanced)
                  }
                }
              } catch (err) {
                console.error('Error enhancing prompt:', err)
              } finally {
                setEnhancingPrompt(false)
              }
            }}
            disabled={enhancingPrompt || !(element.prompt || '').trim()}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Enhance prompt to a detailed 200-300 word description for 4K image generation"
          >
            {enhancingPrompt ? (
              <>
                <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Enhancing...</span>
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Enhance Prompt</span>
              </>
            )}
          </button>
          </div>
        </div>
        <div className="space-y-2">
          <textarea
            value={element.prompt || ''}
            maxLength={2000}
            onChange={(e) => updateElementProperty(selectedElement, 'prompt', e.target.value.slice(0, 2000))}
            placeholder="Enter a prompt to generate this image using AI (e.g., 'A beautiful sunset over mountains with golden light')"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y min-h-[60px]"
            rows={3}
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">This prompt will be used for AI image generation.</p>
            <p className="text-xs text-gray-400">{(element.prompt || '').length}/2000</p>
          </div>
        </div>
      </div>

      {/* Animation Properties */}
      <AnimationPropertiesPanel
        element={element}
        selectedElement={selectedElement}
        updateElementProperty={updateElementProperty}
      />
    </div>
  )
}

export default ImageElementProperties
